import { useEffect, useRef, useState } from 'react';
import { MAX_PAGE_LIMIT } from '../services/api';
import { JobLog } from '../types';

const POLL_INTERVAL_MS = 2000;
const MAX_FINAL_LOAD_ATTEMPTS = 3;

const ACTIVE_STATUSES = ['pending', 'running'];
const FINAL_STATUSES = ['completed', 'failed', 'cancelled'];

// Logs of one job. Polls pages by cursor while the job runs, then loads the
// full list once the job ends.
export function useJobLogPolling(
  jobId: string | undefined,
  status: string | undefined,
  fetchPage: (jobId: string, afterId?: number) => Promise<JobLog[]>,
  fetchAll: (jobId: string) => Promise<JobLog[]>,
): { logs: JobLog[]; failed: boolean } {
  const [logs, setLogs] = useState<JobLog[]>([]);
  const [failed, setFailed] = useState(false);
  const cursorRef = useRef(0);
  // Refs keep the fetchers out of the effect deps, so an inline arrow from the
  // caller does not restart the poll on every render.
  const fetchPageRef = useRef(fetchPage);
  const fetchAllRef = useRef(fetchAll);
  fetchPageRef.current = fetchPage;
  fetchAllRef.current = fetchAll;

  useEffect(() => {
    cursorRef.current = 0;
    setLogs([]);
    setFailed(false);
  }, [jobId]);

  useEffect(() => {
    if (!jobId) return;

    // An unknown status means the job did not load. Asking for its logs would
    // fail the same way, so do nothing until a real status arrives.
    if (!status) return;
    const isFinal = FINAL_STATUSES.includes(status);
    if (!isFinal && !ACTIVE_STATUSES.includes(status)) return;

    let cancelled = false;

    if (isFinal) {
      let loading = false;
      let attempts = 0;
      const loadFinalLogs = async () => {
        if (loading) return;
        loading = true;
        attempts += 1;
        try {
          const finalLogs = await fetchAllRef.current(jobId);
          // Drop a late response: it belongs to the previous job.
          if (cancelled) return;
          setLogs(finalLogs);
          setFailed(false);
          // One good load is enough.
          window.clearInterval(retry);
        } catch (error) {
          // Keep what polling already showed and try again on the next tick.
          if (!cancelled) {
            setFailed(true);
            console.error('Failed to fetch final logs:', error);
            if (attempts >= MAX_FINAL_LOAD_ATTEMPTS) {
              window.clearInterval(retry);
            }
          }
        } finally {
          loading = false;
        }
      };

      // Try at most three times so a persistent failure stops polling.
      const retry = window.setInterval(loadFinalLogs, POLL_INTERVAL_MS);
      loadFinalLogs();

      return () => {
        cancelled = true;
        window.clearInterval(retry);
      };
    }

    let polling = false;
    const pollLogs = async () => {
      if (polling) return;
      polling = true;
      try {
        // Drain full pages: a chatty job can log more than one page per tick.
        for (;;) {
          const nextLogs = await fetchPageRef.current(jobId, cursorRef.current || undefined);
          // Check before any write: a late response must not duplicate lines.
          if (cancelled) return;
          setFailed(false);
          if (nextLogs.length === 0) return;
          setLogs((previous) => [...previous, ...nextLogs]);
          cursorRef.current = nextLogs[nextLogs.length - 1].id;
          if (nextLogs.length < MAX_PAGE_LIMIT) return;
        }
      } catch (error) {
        if (!cancelled) {
          setFailed(true);
          console.error('Failed to fetch logs:', error);
        }
      } finally {
        polling = false;
      }
    };

    pollLogs();
    const interval = window.setInterval(pollLogs, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [jobId, status]);

  return { logs, failed };
}
